import type { IMessageAttachment } from '../redux/buzzbridge/campaignWorkspace.types';

export const MAX_CAMPAIGN_THREAD_LOCATIONS = 3;
export const DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL = 'Shared location';

/** Parsed geographic point from message content or attachment. */
export interface ICampaignThreadLocationPoint {
    lat: number;
    lng: number;
    mapsUrl: string;
    label?: string;
}

const URL_IN_TEXT = /https?:\/\/[^\s<>"']+/gi;

/** Google Maps and common map URL patterns with lat/lng. */
const MAPS_PATTERNS: Array<{ regex: RegExp; parse: (match: RegExpMatchArray) => ICampaignThreadLocationPoint | null }> =
    [
        {
            regex: /https?:\/\/(?:www\.)?maps\.google\.com\/\?q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
            parse: ([, lat, lng]) => buildLocationPoint(Number(lat), Number(lng)),
        },
        {
            regex: /https?:\/\/(?:www\.)?google\.com\/maps\/search\/\?api=1&query=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
            parse: ([, lat, lng]) => buildLocationPoint(Number(lat), Number(lng)),
        },
        {
            regex: /https?:\/\/(?:www\.)?google\.com\/maps\?[^ \n]*[?&]q=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/i,
            parse: ([, lat, lng]) => buildLocationPoint(Number(lat), Number(lng)),
        },
    ];

export function buildGoogleMapsUrl(lat: number, lng: number): string {
    return `https://www.google.com/maps/search/?api=1&query=${lat},${lng}`;
}

export function buildLocationPoint(lat: number, lng: number, label?: string): ICampaignThreadLocationPoint | null {
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
    if (lat < -90 || lat > 90 || lng < -180 || lng > 180) return null;
    return {
        lat,
        lng,
        mapsUrl: buildGoogleMapsUrl(lat, lng),
        label: label?.trim() || undefined,
    };
}

export function parseLocationFromMapsUrl(url: string): ICampaignThreadLocationPoint | null {
    const value = url.trim();
    for (const { regex, parse } of MAPS_PATTERNS) {
        const match = value.match(regex);
        if (match) {
            const point = parse(match);
            if (point) return point;
        }
    }
    return null;
}

/** OpenStreetMap static preview — no API key required. */
export function buildOsmStaticMapUrl(lat: number, lng: number, width = 400, height = 200): string {
    return `https://staticmap.openstreetmap.de/staticmap.php?center=${lat},${lng}&zoom=14&size=${width}x${height}&markers=${lat},${lng},red`;
}

export type CampaignThreadContentSegment =
    | { type: 'text'; value: string }
    | { type: 'link'; href: string; label: string };

const TRAILING_PUNCT = /[),.;!?]+$/;

function cleanUrl(raw: string): string {
    return raw.replace(TRAILING_PUNCT, '');
}

/** Split a line into plain text and clickable URL segments. */
export function linkifyLine(line: string): CampaignThreadContentSegment[] {
    if (!line) return [];
    const segments: CampaignThreadContentSegment[] = [];
    let lastIndex = 0;
    const regex = new RegExp(URL_IN_TEXT.source, 'gi');
    let match: RegExpExecArray | null;
    while ((match = regex.exec(line)) !== null) {
        const raw = match[0];
        const start = match.index;
        if (start > lastIndex) {
            segments.push({ type: 'text', value: line.slice(lastIndex, start) });
        }
        const href = cleanUrl(raw);
        segments.push({ type: 'link', href, label: href });
        lastIndex = start + raw.length;
    }
    if (lastIndex < line.length) {
        segments.push({ type: 'text', value: line.slice(lastIndex) });
    }
    return segments.length ? segments : [{ type: 'text', value: line }];
}

function isLocationOnlyLine(line: string): ICampaignThreadLocationPoint | null {
    const trimmed = line.trim();
    if (!trimmed) return null;
    const withoutPin = trimmed.replace(/^📍\s*/, '').trim();
    const candidate = withoutPin.startsWith('http') ? withoutPin : trimmed.match(URL_IN_TEXT)?.[0];
    if (!candidate) return null;
    const point = parseLocationFromMapsUrl(cleanUrl(candidate));
    if (!point) return null;
    const remainder = trimmed.replace(withoutPin, '').replace('📍', '').trim();
    if (remainder && !/^📍?$/.test(remainder)) return null;
    return point;
}

/**
 * Extract map locations embedded in message prose (legacy client send format).
 * Returns remaining text with location lines removed.
 */
export function extractLocationsFromContent(content: string): {
    locations: ICampaignThreadLocationPoint[];
    remainingContent: string;
} {
    if (!content.trim()) return { locations: [], remainingContent: '' };

    const locations: ICampaignThreadLocationPoint[] = [];
    const seen = new Set<string>();
    const keptLines: string[] = [];

    for (const line of content.split('\n')) {
        const point = isLocationOnlyLine(line);
        if (point) {
            const key = `${point.lat},${point.lng}`;
            if (!seen.has(key)) {
                seen.add(key);
                locations.push(point);
            }
            continue;
        }
        keptLines.push(line);
    }

    return {
        locations,
        remainingContent: keptLines.join('\n').trim(),
    };
}

export function locationFromAttachment(att: {
    kind?: string;
    latitude?: number;
    longitude?: number;
    lat?: number;
    lng?: number;
    label?: string;
    maps_url?: string;
    url?: string;
}): ICampaignThreadLocationPoint | null {
    if (att.kind !== 'location') return null;
    const lat = att.latitude ?? att.lat;
    const lng = att.longitude ?? att.lng;
    if (lat != null && lng != null) {
        const point = buildLocationPoint(Number(lat), Number(lng), att.label ?? DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL);
        if (point && att.maps_url?.trim()) {
            return {
                ...point,
                mapsUrl: att.maps_url.trim(),
                label: point.label ?? DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL,
            };
        }
        return point ? { ...point, label: point.label ?? DEFAULT_CAMPAIGN_THREAD_LOCATION_LABEL } : null;
    }
    if (att.url) return parseLocationFromMapsUrl(att.url);
    return null;
}

export function locationsFromMessageAttachments(attachments: IMessageAttachment[] = []) {
    return attachments
        .filter(att => att.kind === 'location')
        .map(att =>
            locationFromAttachment({
                kind: att.kind,
                latitude: att.latitude,
                longitude: att.longitude,
                lat: att.lat,
                lng: att.lng,
                label: att.label ?? att.filename,
                maps_url: att.maps_url,
            }),
        )
        .filter((point): point is ICampaignThreadLocationPoint => point != null);
}

export function mergeLocationPoints(...groups: ICampaignThreadLocationPoint[][]): ICampaignThreadLocationPoint[] {
    const seen = new Set<string>();
    const out: ICampaignThreadLocationPoint[] = [];
    for (const group of groups) {
        for (const point of group) {
            const key = `${point.lat},${point.lng}`;
            if (seen.has(key)) continue;
            seen.add(key);
            out.push(point);
        }
    }
    return out;
}

export function formatLocationSendLine(lat: number, lng: number): string {
    return `📍 ${buildGoogleMapsUrl(lat, lng)}`;
}
